import type { OffsetBasedPage } from "@shared/types/pagination.ts";

/** Generic paginated response envelope returned by all listing endpoints. */
export interface PageResponse<T> {
  items: T[];
  total: number;
}

/**
 * What a list that loads more as the reader scrolls needs to know and to be told: whether there is
 * more, whether it is loading, whether loading failed, and what to call when the reader reaches the
 * end. A hook returns it and a list component takes it as it is.
 */
export interface InfiniteScroll {
  hasMore: boolean;
  /** A further page is loading. */
  loadingMore: boolean;
  /** The message of the last failed attempt to load more; null once another attempt starts. */
  error: string | null;
  /** The reader has reached the end of what is loaded; also what retrying calls. */
  onReachEnd: () => void;
}

/** Rows-per-page choices offered by lists whose page size is user-selectable. */
export const PAGE_SIZE_OPTIONS = [25, 50, 100];

/** A paginated list's position and page controls, as a list renders them. Pages are 1-based. */
export interface ListPagination {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Rows-per-page choices; the page size is fixed when omitted. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
}

/** Appends non-null offset page fields into an existing URLSearchParams. */
export function appendOffsetParams(qs: URLSearchParams, page: OffsetBasedPage): void {
  if (page.limit !== undefined) qs.set("limit", String(page.limit));
  if (page.offset !== undefined) qs.set("offset", String(page.offset));
}
